import { useEffect, useState } from 'react';

/**
 * "A new version is ready": the app bundle is content-hashed (/assets/index-<hash>.js), so a deploy
 * changes the script index.html points at. index.html is served no-store, so re-reading it tells us
 * whether the running tab is behind. No version file, no server change, nothing to bump by hand.
 */
const BUNDLE = /\/assets\/index-[\w-]+\.js/;
const EVERY_MS = 5 * 60 * 1000;

/** The entry bundle named in an index.html (or null in dev, where there is none). */
export function bundleIn(html: string): string | null {
  return html.match(BUNDLE)?.[0] ?? null;
}

/** The entry bundle this tab is running. */
export function runningBundle(): string | null {
  for (const s of document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const m = new URL(s.src, location.href).pathname.match(BUNDLE);
    if (m) return m[0];
  }
  return null;
}

/** True once a newer deploy is live than the one this tab loaded. Checks every 5 minutes and when the tab comes back. */
export function useNewVersion(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const running = runningBundle();
    if (!running || ready) return;
    let busy = false;
    const check = async () => {
      if (busy || document.visibilityState === 'hidden') return;
      busy = true;
      try {
        const res = await fetch(`/?v=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
        const latest = res.ok ? bundleIn(await res.text()) : null;
        if (latest && latest !== running) setReady(true);
      } catch { /* offline or mid-deploy: try again next time */ } finally { busy = false; }
    };
    const timer = window.setInterval(check, EVERY_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', check); window.removeEventListener('focus', check); };
  }, [ready]);
  return ready;
}
